import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Brand } from '../components/Brand';
import { REGIONS, selectRegion } from '../config/regions';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { ChoiceCard } from '../components/ChoiceCard';

export function RegionPage() {
  const { state, update } = useApp();
  const navigate = useNavigate();
  const selected = state.region;

  const choose = (region) => update({ region });
  const continueNext = () => {
    if (!selected) return;

    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || localStorage.getItem('nudge_referral_code');

    if (ref) {
      localStorage.setItem('nudge_referral_code', ref);
    }

    const signinPath = `/signin${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;

    const globalOrigin =
      import.meta.env.VITE_REACT_APP_GLOBAL_APP_URL ||
      'https://app.nudgeforme.com';

    const euOrigin =
      import.meta.env.VITE_REACT_APP_EU_APP_URL ||
      'https://europe.nudgeforme.com';

    const targetOrigin = selected === 'eu' ? euOrigin : globalOrigin;
    const currentOrigin = window.location.origin;

    // Same app/domain, just go to signin.
    if (currentOrigin === targetOrigin) {
      navigate(signinPath, { replace: true });
      return;
    }

    // Different region/domain, redirect there and preserve referral.
    window.location.assign(`${targetOrigin}${signinPath}`);
  };


  return (
    <Grid container minHeight="100vh">
      <Grid item xs={12} md={5} className="night-panel">
        <Box sx={{ position: 'relative', zIndex: 1, minHeight: { xs: 300, md: '100vh' }, p: { xs: 3, md: 6 }, display: 'flex', flexDirection: 'column' }}>
          <Brand light />
          <Box sx={{ mt: 'auto', mb: { xs: 2, md: 5 }, maxWidth: 480 }}>
            <Typography className="mono-label" sx={{ color: 'primary.main', mb: 2 }}>Choose your data region</Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 58 }, color: '#eef0ff' }}>Your inbox. Your region.</Typography>
            <Typography sx={{ mt: 2, color: '#a0a5c8', fontSize: 17 }}>
              Nudge uses the same regional Snoooz infrastructure as your mailbox connection. You can change products later without reconnecting your inbox.
            </Typography>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={7}>
        <Container maxWidth="sm" sx={{ minHeight: { md: '100vh' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 6 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: 34, md: 44 } }}>Where should Nudge store your data?</Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5, mb: 4, fontSize: 16.5 }}>
            Choose the environment that matches your privacy and residency requirements.
          </Typography>

          <Stack spacing={2}>
            <ChoiceCard
              selected={selected === 'global'}
              onClick={() => choose('global')}
              title={REGIONS.global.title}
              description={REGIONS.global.description}
              icon={<Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(141,149,255,.13)', color: 'secondary.main' }}><PublicRoundedIcon /></Box>}
            />
            <ChoiceCard
              selected={selected === 'eu'}
              onClick={() => choose('eu')}
              title={REGIONS.eu.title}
              description={REGIONS.eu.description}
              icon={<Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(70,201,142,.12)', color: 'success.main' }}><ShieldOutlinedIcon /></Box>}
            />
          </Stack>

          <Button
            type="button"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardRoundedIcon />}
            disabled={!selected}
            onClick={continueNext}
            sx={{ mt: 4, alignSelf: 'flex-start' }}
          >
            Continue
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Existing Snoooz customers should choose the same region as their Snoooz account.
          </Typography>
        </Container>
      </Grid>
    </Grid>
  );
}
